import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Typography,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  FormHelperText,
  Tooltip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Card,
  CardContent,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add, Edit, Business, Home, Block, CheckCircle } from '@mui/icons-material';
import { usersAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { buildingsAPI } from '../services/api';
import './Users.css';

const userTypes = [
  { value: 'BUILDING_ADMIN', label: 'Building Admin' },
  { value: 'ADMIN', label: 'Admin' }
];

const safeArray = (val) => Array.isArray(val) ? val : [];
const adminUserTypes = new Set(userTypes.map(type => type.value));
const filterAdminUsers = (users) => safeArray(users).filter(user => adminUserTypes.has(user.userType));

const Users = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [open, setOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '+91',
    userType: 'ADMIN',
    buildingId: '', // <-- single buildingId
  });
  const [error, setError] = useState('');
  const [selectedBuilding, setSelectedBuilding] = useState('');
  const [filteredUsers, setFilteredUsers] = useState(null);
  const [filterError, setFilterError] = useState('');
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [associationAdded, setAssociationAdded] = useState(false);

  const queryClient = useQueryClient();
  const { data: users = [], isLoading: isUsersLoading } = useQuery('users', async () => (await usersAPI.getAll()).data);
  const { data: buildings = [], isLoading: isBuildingsLoading } = useQuery('buildings', async () => (await buildingsAPI.getAll()).data);

  const requiresPropertySelection = formData.userType === 'OWNER' || formData.userType === 'TENANT';

  // Always call hooks first!
  const createMutation = useMutation(usersAPI.create, {
    onSuccess: () => {
      queryClient.invalidateQueries('users');
      handleClose();
    },
    onError: (error) => {
      setError(error.response?.data?.message || 'Failed to create user');
    },
  });

  const updateMutation = useMutation(
    ({ id, data }) => usersAPI.update(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users');
        handleClose();
      },
      onError: (error) => {
        setError(error.response?.data?.message || 'Failed to update user');
      },
    }
  );

  const toggleActiveMutation = useMutation(
    ({ id, isActive }) => usersAPI.update(id, { isActive }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users');
      },
      onError: (error) => {
        setError(error.response?.data?.message || 'Failed to update user status');
      },
    }
  );

  const addAssociationsMutation = useMutation(
    ({ userId, data }) => usersAPI.addAssociations(userId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users');
      },
      onError: (error) => {
        setError(error.response?.data?.message || 'Failed to add associations');
      },
    }
  );

  // Extract group names - handle both old format (strings) and new format (objects)
  const userGroups = user?.groups || [];
  const groupNames = userGroups.map(g => typeof g === 'string' ? g : g.name);
  const isAdminOrStaff = user && (groupNames.some(g => ['admin', 'maintenance_staff', 'building_admin'].includes(g.toLowerCase())) ||
                                 ['super_admin', 'building_admin', 'maintenance_staff'].includes(user?.role));
  const handleChange = (event) => {
    /* const selectedIndex = event.target.selectedIndex;
    const selectedOption = event.target.options[selectedIndex];
    const dataKey = selectedOption.getAttribute('data-key');
    const value = event.target.value;

    console.log('Selected value:', value);      // buildingId
    console.log('Selected data-key:', dataKey); // buildingId (same as value here)
 */
    console.log('event.target.value:', event.target.value);
    const [buildingName, buildingId] = event.target.value.split('|');
    //const buildingValue = event.target.value;
    //const buildingId = event.target.id;
    console.log('Selected building name:', buildingName);
    console.log('Selected building id:', buildingId);
    //const selectedIndex = event.target.options.selectedIndex;
    //const buildingId = event.target.options[selectedIndex].getAttribute('data-key');
    //console.log();
    setSelectedBuilding(event.target.value);
    setFilteredUsers(null);
    setError('');
    if (buildingId) {
      usersAPI.getUsers(buildingId)
        .then((usersRes) => {
          setFilteredUsers(safeArray(usersRes.data));
          if (usersRes.data.length === 0) {
            setError('No users found for selected building.');
          }
        })
        .catch((err) => {
          setError(err.response?.data?.message || 'Failed to load users');
          setFilteredUsers([]);
        });
    } else {
      setFilteredUsers(null);
    }
  };
  const handleFilter = (buildingId) => {
    setFilterError('');
    if (!buildingId) {
      setFilteredUsers(null);
      setFilterError('Please select a building.');
      return;
    }
    usersAPI.getUsers(buildingId)
      .then((usersRes) => {
        setFilteredUsers(safeArray(usersRes.data));
        if (usersRes.data.length === 0) {
          setError('No users found for selected building.');
        }
      })
      .catch((err) => {
        setError(err.response?.data?.message || 'Failed to load users');
        setFilteredUsers([]);
      });
  };

  // Handle building selection change
  const handleBuildingChange = (event) => {
    const selectedBuildingId = event.target.value;
    setFormData(prev => ({
      ...prev,
      buildingId: selectedBuildingId
    }));
  };

  const handleToggleActive = (userRow) => {
    const action = userRow.isActive ? 'deactivate' : 'reactivate';
    const confirmMessage = `Are you sure you want to ${action} ${userRow.firstName} ${userRow.lastName}?`;
    
    if (window.confirm(confirmMessage)) {
      toggleActiveMutation.mutate({
        id: userRow.userId,
        isActive: !userRow.isActive
      });
    }
  };

  if (users && users.message) {
    return <Typography color="error">{users.message}</Typography>;
  }
  // Only show admin users from selected building, no fallback to all users
  const usersSafe = selectedBuilding && Array.isArray(filteredUsers)
    ? filterAdminUsers(filteredUsers)
    : []; // Empty array when no building is selected
  const buildingsSafe = safeArray(buildings);

  const handleOpen = (user = null, buildingId = null) => {
    // Pass selected building to form for better UX
    if (user) {
      setEditingUser(user);
      // Format phone number to ensure it has +91 prefix
      let phoneNumber = user.phoneNumber || '+91';
      if (phoneNumber && !phoneNumber.startsWith('+91')) {
        // If the number doesn't start with +91, add it
        phoneNumber = '+91' + phoneNumber.replace(/^\+?91?/, '');
      }
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phoneNumber: phoneNumber,
        userType: user.userType || 'ADMIN',
        buildingId: buildingId || user.buildingId || '', // Use passed buildingId or user.buildingId
      });
    } else {
      setEditingUser(null);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '+91',
        userType: 'ADMIN',
        buildingId: buildingId || '', // Use passed buildingId if provided
      });
    }
    setError('');
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingUser(null);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '+91',
      userType: 'ADMIN',
      buildingId: '',
    });
    setError('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate phone number format
    if (!formData.phoneNumber || formData.phoneNumber.length !== 13 || !formData.phoneNumber.startsWith('+91')) {
      setError('Please enter a valid phone number with +91 followed by 10 digits.');
      return;
    }

    // Validate that the number part contains only digits
    const numberPart = formData.phoneNumber.substring(3);
    if (!/^\d{10}$/.test(numberPart)) {
      setError('Please enter exactly 10 digits after +91.');
      return;
    }

    // Validate property selection only for OWNER or TENANT
    if (requiresPropertySelection) {
      if (!formData.buildingId) {
        setError('Please select a building for Owner/Tenant users.');
        return;
      }
    }

    if (editingUser) {
      updateMutation.mutate({
        id: editingUser.userId,
        data: {
          ...formData,
          buildingIds: formData.buildingId ? [formData.buildingId] : []
        }
      });
    } else {
      createMutation.mutate({
        ...formData,
        buildingIds: formData.buildingId ? [formData.buildingId] : []
      });
    }
  };

  const columns = [
    { 
      field: 'name', 
      headerName: 'Name', 
      width: 150,
      renderCell: (params) => {
        const fullName = `${params.row.firstName || ''} ${params.row.lastName || ''}`.trim();
        if (fullName.length > 10) {
          // Find a good break point around 10 characters
          let breakPoint = 10;
          // Try to break at a space near the 10-character mark
          for (let i = 8; i <= 12 && i < fullName.length; i++) {
            if (fullName[i] === ' ') {
              breakPoint = i;
              break;
            }
          }
          // Split the name and display with line break
          let firstPart, secondPart;
          if (breakPoint === 10 && fullName[10] !== ' ') {
            firstPart = fullName.substring(0, 10);
            secondPart = fullName.substring(10);
          } else {
            firstPart = fullName.substring(0, breakPoint);
            secondPart = fullName.substring(breakPoint + 1);
          }
          return (
            <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.2' }}>
              {firstPart}
              {'\n'}
              {secondPart}
            </div>
          );
        }
        return fullName;
      }
    },
    { 
      field: 'email', 
      headerName: 'Email', 
      width: 200,
      renderCell: (params) => {
        const email = params.value || '';
        if (email.length > 10) {
          // Find a good break point around 10 characters
          let breakPoint = 10;
          // Try to break at @ symbol or . near the 10-character mark
          for (let i = 8; i <= 12 && i < email.length; i++) {
            if (email[i] === '@' || email[i] === '.') {
              breakPoint = i;
              break;
            }
          }
          // Split the email and display with line break
          let firstPart, secondPart;
          if (breakPoint === 10 && email[10] !== '@' && email[10] !== '.') {
            firstPart = email.substring(0, 10);
            secondPart = email.substring(10);
          } else {
            firstPart = email.substring(0, breakPoint);
            secondPart = email.substring(breakPoint);
          }
          return (
            <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.2' }}>
              {firstPart}
              {'\n'}
              {secondPart}
            </div>
          );
        }
        return email;
      }
    },
    { field: 'phoneNumber', headerName: 'Phone', width: 150 },
    { field: 'userType', headerName: 'Type', width: 150 },
    { 
      field: 'isActive', 
      headerName: 'Status', 
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'error'}
          size="small"
          variant="outlined"
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Edit User">
            <IconButton onClick={() => handleOpen(params.row, selectedBuilding)} size="small">
              <Edit />
            </IconButton>
          </Tooltip>
          <Tooltip title={params.row.isActive ? 'Deactivate User' : 'Activate User'}>
            <IconButton 
              onClick={() => handleToggleActive(params.row)} 
              color={params.row.isActive ? 'warning' : 'success'}
              size="small"
              disabled={toggleActiveMutation.isLoading}
            >
              {params.row.isActive ? <Block /> : <CheckCircle />}
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant={isMobile ? "h5" : "h4"}>Users Management</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpen(null, selectedBuilding)}
          sx={{ minHeight: isMobile ? 44 : 'auto', fontSize: isMobile ? '14px' : '16px' }}
        >
          Add User
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {associationAdded && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Association added!
        </Alert>
      )}

      {isAdminOrStaff && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
          {/* <select value={selectedBuilding} onChange={handleChange}>
      <option value="">-- Select Building --</option>
      {buildings.map(b => (
        <option key={b.buildingId} value={b.buildingName} data-key={b.buildingId}>
          {b.buildingName}
        </option>
      ))}
    </select> */
        <TextField
          select
          label="Select Building"
          value={selectedBuilding}
          onChange={e => {
            setSelectedBuilding(e.target.value);
            if (e.target.value) {
              handleFilter(e.target.value);
            } else {
              setFilteredUsers(null); // <-- Clear filtered users when no building selected
              setError('');
            }
          }}
          disabled={isBuildingsLoading}
          sx={{ minWidth: 250 }}
        >
          <MenuItem value="">-- Select --</MenuItem>
          {Array.isArray(buildingsSafe) && buildingsSafe.length > 0 ? (
            buildingsSafe.map(b => (
              <MenuItem key={b.buildingId} value={b.buildingId}>
                {b.buildingName}
              </MenuItem>
            ))
          ) : null}
        </TextField>
    }
          
          {/* <Button
            variant="outlined"
            onClick={handleFilter}
            disabled={isBuildingsLoading || isFlatsLoading || isUsersLoading || !Array.isArray(buildingsSafe) || buildingsSafe.length === 0}
          >
            Filter Users by Building
          </Button> */}
          {(!isBuildingsLoading && Array.isArray(buildingsSafe) && buildingsSafe.length === 0) && (
            <Typography color="warning.main">No buildings available for selection.</Typography>
          )}
          {filterError && <Typography color="error">{filterError}</Typography>}
        </Box>
      )}

      {!selectedBuilding && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Please select a building above to view and manage users for that building.
        </Alert>
      )}

      <Box sx={{ height: isMobile ? 'auto' : 600, width: '100%' }}>
        {isMobile ? (
          // Mobile card layout
          <Box>
            {usersSafe.length === 0 ? (
              <Card sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2">No users found</Typography>
              </Card>
            ) : (
              usersSafe.map((user) => (
                <Card key={user.userId} sx={{ mb: 2 }}>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '14px' }}>
                        {`${user.firstName || ''} ${user.lastName || ''}`.trim()}
                      </Typography>
                      <Chip
                        label={user.isActive ? 'Active' : 'Inactive'}
                        color={user.isActive ? 'success' : 'error'}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '11px' }}
                      />
                    </Box>
                    
                    <Typography variant="body2" sx={{ fontSize: '12px', color: 'text.secondary', mb: 0.5 }}>
                      {user.email}
                    </Typography>
                    
                    <Typography variant="body2" sx={{ fontSize: '12px', color: 'text.secondary', mb: 1 }}>
                      {user.phoneNumber} • {user.userType}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                      <Tooltip title="Edit User">
                        <IconButton onClick={() => handleOpen(user, selectedBuilding)} size="small" sx={{ minHeight: 44 }}>
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={user.isActive ? 'Deactivate User' : 'Activate User'}>
                        <IconButton 
                          onClick={() => handleToggleActive(user)} 
                          color={user.isActive ? 'warning' : 'success'}
                          size="small"
                          disabled={toggleActiveMutation.isLoading}
                          sx={{ minHeight: 44 }}
                        >
                          {user.isActive ? <Block /> : <CheckCircle />}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </CardContent>
                </Card>
              ))
            )}
          </Box>
        ) : (
          // Desktop DataGrid layout
          <DataGrid
            rows={usersSafe}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            loading={isUsersLoading}
            getRowId={(row) => {
              if (row.userId) return row.userId;
              if (row.id) return row.id;
              console.warn('Row missing userId, generating fallback:', row);
              return `fallback_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
            }}
            onRowClick={(params) => setSelectedRowId(params.id)}
            onCellClick={(params, event) => {
              // Prevent selection if clicking on a button inside the cell
              if (event.target.closest('button')) return;
              setSelectedRowId(params.id);
            }}
            onRowSelectionModelChange={() => setSelectedRowId(null)} // Unselect on selection change
            getRowClassName={(params) =>
              params.id === selectedRowId ? 'selected-row' : ''
            }
          />
        )}
      </Box>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ 
          position: isMobile ? 'sticky' : 'static',
          top: 0,
          zIndex: 1,
          backgroundColor: 'background.paper',
          borderBottom: isMobile ? 1 : 0,
          borderColor: 'divider'
        }}>
          {editingUser ? 'Edit User' : 'Add New User'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ p: isMobile ? 2 : 3 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: '1fr 1fr' }}>
              <TextField
                label="First Name"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                required
                fullWidth
              />
              <TextField
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                required
                fullWidth
              />
              <TextField
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                fullWidth
              />
              <TextField
                label="Phone Number"
                value={formData.phoneNumber}
                onChange={(e) => {
                  let value = e.target.value;
                  // Ensure it always starts with +91
                  if (!value.startsWith('+91')) {
                    value = '+91';
                  }
                  // Limit to +91 + 10 digits
                  if (value.length > 13) {
                    value = value.substring(0, 13);
                  }
                  // Only allow numbers after +91
                  const numberPart = value.substring(3);
                  if (numberPart && !/^\d*$/.test(numberPart)) {
                    return; // Don't update if non-numeric characters are entered
                  }
                  setFormData({ ...formData, phoneNumber: value });
                }}
                placeholder="+91XXXXXXXXXX"
                inputProps={{
                  maxLength: 13,
                  pattern: "\\+91[0-9]{10}"
                }}
                helperText="Enter 10 digits after +91"
                required
                fullWidth
              />
              <TextField
                select
                label="User Type"
                value={formData.userType}
                onChange={(e) => setFormData({ ...formData, userType: e.target.value })}
                required
                fullWidth
              >
                {userTypes.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            
            {/* --- Add this block for Building(s) selection --- */}
            <Box sx={{ mt: 3 }}>
              <FormControl fullWidth>
                <InputLabel id="building-select-label">Select Building</InputLabel>
                <Select
                  labelId="building-select-label"
                  value={formData.buildingId}
                  onChange={handleBuildingChange}
                  input={<OutlinedInput label="Select Building" />}
                >
                  {buildingsSafe.map(b => (
                    <MenuItem key={b.buildingId} value={b.buildingId}>
                      {b.buildingName}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {requiresPropertySelection ? 'Required for Owner/Tenant' : 'Optional'}
                </FormHelperText>
              </FormControl>
            </Box>
            {/* --- End building selection block --- */}

            
          </DialogContent>
          <DialogActions sx={{ 
            position: isMobile ? 'sticky' : 'static',
            bottom: 0,
            backgroundColor: 'background.paper',
            borderTop: isMobile ? 1 : 0,
            borderColor: 'divider',
            p: isMobile ? 2 : 1
          }}>
            <Button onClick={handleClose} sx={{ minHeight: isMobile ? 44 : 'auto' }}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={createMutation.isLoading || updateMutation.isLoading}
              sx={{ minHeight: isMobile ? 44 : 'auto' }}
            >
              {editingUser ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Users;
